import React, { useEffect, useMemo } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import { appointmentsProgramsDataSelector } from "../../../../services/selectors/utils.selectors";
import {
  useGetAllMetaTablesQuery,
  useGetProtocolsByCompanyQuery,
  useConvertToSeriesMutation,
  useLazyGetProgramConfigQuery,
} from "../../../../services/api/utils.api";
import {
  ISelectedProtocol,
  ProtocolFrequencyType,
  TProtocolFrequencyType,
  IAppointmentProgramProtocol,
  IConvertToSeriesRequest,
} from "../../../../services/types/utils.types";
import TreatmentPlanModal, {
  ITreatmentPlanValues,
} from "../TreatmentPlanModal/TreatmentPlanModal";

interface IConvertToSeriesModalProps {
  appointmentId: number;
  /** When present, the appointment already has a program (single + protocols) to update. */
  appointmentsProgramsId?: number | null;
  durationMinutes: number;
  mediaLabel?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const numericToFreq = (ft?: number): ProtocolFrequencyType => {
  if (ft === 2) return "sequential";
  if (ft === 3) return "afterFirstOnce";
  return "every";
};

// ISelectedProtocol (UI) → the backend protocol payload.
const buildProtocolsPayload = (
  protocols: ISelectedProtocol[]
): IAppointmentProgramProtocol[] =>
  protocols.map((p) => {
    switch (p.frequencyType) {
      case "sequential":
        return {
          protocolId: p.protocolId,
          frequencyType: 2 as TProtocolFrequencyType,
          startAtSession: 1,
          everyNSessions: p.everyNSessions,
        };
      case "afterFirstOnce":
        return {
          protocolId: p.protocolId,
          frequencyType: 3 as TProtocolFrequencyType,
          startAtSession: 2,
          everyNSessions: null,
        };
      case "afterFirst":
        return {
          protocolId: p.protocolId,
          frequencyType: 1 as TProtocolFrequencyType,
          startAtSession: 2,
          everyNSessions: null,
        };
      default:
        return {
          protocolId: p.protocolId,
          frequencyType: 1 as TProtocolFrequencyType,
          startAtSession: 1,
          everyNSessions: null,
        };
    }
  });

/**
 * Container for "convert single appointment to a series". Fetches the meta tables,
 * protocols, and (for a single-with-protocol) the existing program config to pre-fill,
 * then renders the shared TreatmentPlanModal in convert mode and posts the conversion.
 */
const ConvertToSeriesModal: React.FC<IConvertToSeriesModalProps> = ({
  appointmentId,
  appointmentsProgramsId,
  durationMinutes,
  mediaLabel = "",
  onClose,
  onSuccess,
}) => {
  const admin = useAppSelector(adminSelector);
  const appointmentsProgramsData = useAppSelector(appointmentsProgramsDataSelector);

  useGetAllMetaTablesQuery(admin.companyId, { skip: !admin.companyId });
  const { data: protocolsData = [] } = useGetProtocolsByCompanyQuery(
    { companyId: admin.companyId },
    { skip: !admin.companyId }
  );

  const [convertToSeries] = useConvertToSeriesMutation();
  const [fetchConfig, { data: config, isFetching: isLoadingConfig }] =
    useLazyGetProgramConfigQuery();

  const hasProgram = !!appointmentsProgramsId;

  useEffect(() => {
    if (hasProgram && appointmentsProgramsId) {
      fetchConfig(appointmentsProgramsId);
    }
  }, [hasProgram, appointmentsProgramsId, fetchConfig]);

  const durationOptions = useMemo(
    () =>
      appointmentsProgramsData.AppointmentsDurations.map((d) => ({
        value: Number(d.value ?? d.Id),
        label: `${d.Minutes ?? d.label} ${t("MINUTES_SUFFIX")}`,
      })),
    [appointmentsProgramsData.AppointmentsDurations]
  );

  const sequenceOptions = useMemo(
    () =>
      appointmentsProgramsData.AppointmentsSequence.map((s) => ({
        value: Number(s.value ?? s.Id),
        label: String(s.label ?? s.CountInPeriod),
      })),
    [appointmentsProgramsData.AppointmentsSequence]
  );

  // Case B — pre-fill from the existing program config.
  const initialDetails = useMemo(() => {
    if (!config?.success) return undefined;
    return {
      treatmentPlanName: config.name ?? undefined,
      roleId: config.roleId ?? undefined,
      genderId: config.genderId ?? undefined,
      migzarId: config.migzarId ?? undefined,
      treatmentRoomId: config.treatmentRoomId ?? undefined,
      fundingSourcesId: config.fundingSourcesId ?? undefined,
      treatmentTypesId: config.treatmentTypesId ?? undefined,
    };
  }, [config]);

  const initialProtocols = useMemo<ISelectedProtocol[] | undefined>(() => {
    if (!config?.success || !config.protocols?.length) return undefined;
    return config.protocols.map((p) => ({
      protocolId: p.protocolId,
      name: protocolsData.find((x) => x.id === p.protocolId)?.desc ?? "",
      frequencyType: numericToFreq(p.frequencyType),
      everyNSessions: p.everyNSessions ?? 2,
    }));
  }, [config, protocolsData]);

  const handleSave = (v: ITreatmentPlanValues) => {
    const body: IConvertToSeriesRequest = {
      appointmentsSequenceId: v.appointmentsSequenceId,
      countOfAppointments: v.countOfAppointments ?? 2,
      appointmentsDurationMinutes: durationMinutes,
      treatmentPlanName: v.treatmentPlanName,
      roleId: v.roleId,
      genderId: v.genderId,
      migzarId: v.migzarId,
      treatmentRoomId: v.treatmentRoomId,
      fundingSourcesId: v.fundingSourcesId,
      treatmentTypesId: v.treatmentTypesId,
      protocols: buildProtocolsPayload(v.protocols),
    };
    convertToSeries({ appointmentId, body })
      .unwrap()
      .then(() => onSuccess())
      .catch(() => {
        /* silent — the details modal stays; user can retry */
      });
  };

  // For case B, wait for the config so the modal seeds its prefill on mount.
  if (hasProgram && (isLoadingConfig || !config)) return null;

  return (
    <TreatmentPlanModal
      companyId={admin.companyId}
      repeatCount={2}
      mode="convert"
      appointmentsDurationsId={0}
      appointmentsSequenceId={0}
      calculatedDurationMinutes={durationMinutes}
      selectedProtocols={[]}
      initialProtocols={initialProtocols}
      initialDetails={initialDetails}
      durationOptions={durationOptions}
      sequenceOptions={sequenceOptions}
      protocols={protocolsData}
      roleOptions={appointmentsProgramsData.Roles}
      genderOptions={appointmentsProgramsData.Genders}
      migzarOptions={appointmentsProgramsData.Migzars}
      treatmentRoomOptions={appointmentsProgramsData.TreatmentRoomsList}
      fundingSourcesOptions={appointmentsProgramsData.FundingSources}
      treatmentTypesOptions={appointmentsProgramsData.TreatmentTypes}
      mediaLabel={mediaLabel}
      onClose={onClose}
      onSave={handleSave}
    />
  );
};

export default ConvertToSeriesModal;
